import React from 'react';

// material-ui
import { useTheme, styled, keyframes } from '@mui/material/styles';
import { alpha, Box, Button, InputBase, Popper, Fade, Card, CardContent, Grid } from '@mui/material';
import PopupState, { bindToggle, bindPopper } from 'material-ui-popup-state';

// assets
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';

// Premium Animations
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.3); }
  70% { box-shadow: 0 0 0 6px rgba(25, 118, 210, 0); }
  100% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-4px); }
`;

// ==============================|| SEARCH SECTION ||============================== //

const SearchSection = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 2,
        backgroundColor: { sm: alpha(theme.palette.common.white, 0.15), xs: 'transparent' },
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        '&:hover': {
          backgroundColor: { sm: alpha(theme.palette.common.white, 0.25), xs: 'transparent' },
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        },
        ml: { sm: theme.spacing(1), xs: 0 },
        mr: { sm: theme.spacing(2), xs: 0 },
        width: 'auto',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: 'linear-gradient(90deg, #1976d2, #42a5f5, #1976d2)',
          backgroundSize: '200% 100%',
          animation: `${shimmer} 3s ease-in-out infinite`,
          borderRadius: '2px 2px 0 0'
        }
      }}
    >
      <Box sx={{ display: { sm: 'none', xs: 'block' } }}>
        <PopupState variant="popper" popupId="demo-popup-popper">
          {(popupState) => (
            <>
              <Button
                sx={{
                  minWidth: { xs: 35 }
                }}
                aria-haspopup="true"
                {...bindToggle(popupState)}
                color="inherit"
              >
                <SearchTwoToneIcon sx={{ fontSize: '1.5rem' }} />
              </Button>
              <Popper
                {...bindPopper(popupState)}
                transition
                sx={{ zIndex: 1201, width: '100%', top: '10px !important', p: 0 }}
                modifiers={[
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 10]
                    }
                  },
                  {
                    name: 'preventOverflow',
                    options: {
                      altAxis: true
                    }
                  }
                ]}
              >
                {({ TransitionProps }) => (
                  <Fade {...TransitionProps} timeout={350}>
                    <Card
                      sx={{
                        borderRadius: 0,
                        background: theme.palette.primary[200],
                        border: { sm: 0 },
                        boxShadow: { sm: 'none' }
                      }}
                    >
                      <CardContent sx={{ p: 1.5, backgroundColor: theme.palette.secondary.main }}>
                        <Grid container alignItems="center" justifyContent="space-between">
                          <Grid item xs>
                            <Box display="flex" p={0}>
                              <Box
                                sx={{
                                  p: { sm: theme.spacing(0.75, 1.25), xs: theme.spacing(1.25) },
                                  position: 'absolute',
                                  pointerEvents: 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: theme.palette.grey[100]
                                }}
                              >
                                <SearchTwoToneIcon color="inherit" />
                              </Box>
                              <InputBase
                                fullWidth
                                placeholder="Search…"
                                sx={{
                                  '& .MuiInputBase-root': {
                                    color: 'inherit'
                                  },
                                  '& .MuiInputBase-input': {
                                    padding: theme.spacing(1, 1, 1, 0),
                                    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
                                    transition: theme.transitions.create('width'),
                                    color: theme.palette.grey[100],
                                    width: { sm: '100%', md: 125 },
                                    mr: 3,
                                    '&:focus': {
                                      width: { md: 225 }
                                    }
                                  }
                                }}
                                inputProps={{ 'aria-label': 'search' }}
                              />
                              <Box
                                sx={{
                                  cursor: 'pointer',
                                  p: theme.spacing(1.25),
                                  alignItems: 'center',
                                  justifyContent: 'flex-end',
                                  display: 'flex',
                                  color: theme.palette.grey[100]
                                }}
                                {...bindToggle(popupState)}
                              >
                                <CloseTwoToneIcon color="inherit" />
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Fade>
                )}
              </Popper>
            </>
          )}
        </PopupState>
      </Box>
      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
        <Box
          sx={{
            p: { sm: theme.spacing(0.75, 1.25), xs: theme.spacing(1.25) },
            position: 'absolute',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.grey[100],
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '120%',
              height: '120%',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(25, 118, 210, 0.1) 0%, transparent 70%)',
              animation: `${pulse} 2s ease-in-out infinite`,
              zIndex: -1
            }
          }}
        >
          <SearchTwoToneIcon sx={{ 
            animation: `${float} 3s ease-in-out infinite`,
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
          }} />
        </Box>
        <InputBase
          placeholder="Search…"
          sx={{
            '& .MuiInputBase-root': {
              color: 'inherit',
              mr: 3
            },
            '& .MuiInputBase-input': {
              p: theme.spacing(1, 1, 1, 0),
              pl: `calc(1em + ${theme.spacing(4)})`,
              transition: theme.transitions.create('width'),
              color: theme.palette.grey[100],
              width: { sm: '100%', md: 125 },
              mr: { md: 3 },
              '&:focus': {
                width: { md: 225 }
              }
            }
          }}
          inputProps={{ 'aria-label': 'search' }}
        />
      </Box>
    </Box>
  );
};

export default SearchSection;
